import { useMemo, useState } from 'react';
import { formatDate } from '../lib/format';
import { useAuthedImage, useResource } from '../lib/hooks';
import type { WorkflowRule } from '../lib/types';
import { Empty, ErrorBanner, Loading } from './ui';

/**
 * The PSA workflow rule catalogue — manual section 1C.1.
 *
 * It replaces Workflow Mapping.xlsx: an index sheet and eighty-seven tabs,
 * each drawing the same trigger → condition → action → end diagram out of
 * merged cells. The workbook was unreadable in the way large workbooks are —
 * you could only see one rule at a time, and only if you already knew which
 * tab it was on. So the index is the thing that got the attention here:
 * search covers every field of every rule, not just the names, because
 * "which rule emails escalation@" is the question people actually arrive
 * with and the name never answers it.
 *
 * THE QUESTIONS IN THE MARGINS ARE PROMOTED, not preserved. In the workbook
 * they sat in column P of individual tabs — "why does escalations get this",
 * "this seems like a ton of bureaucracy" — where nobody would ever find them.
 * Thirty-three of the eighty-seven carry one. They are counted on the index,
 * filterable on their own, and shown above the rule rather than below it.
 *
 * The screenshots came with them — 34 pictures of the e-mail each rule sends,
 * behind a toggle on the notification panel. See migration 096 for why they
 * were nearly missed.
 */
export function WorkflowRules() {
  const rules = useResource<WorkflowRule[]>('/manual/workflow-rules');
  const [code, setCode] = useState<string | null>(null);
  const [term, setTerm] = useState('');
  const [kind, setKind] = useState<string | null>(null);
  const [questionsOnly, setQuestionsOnly] = useState(false);

  const all = useMemo(() => rules.data ?? [], [rules.data]);

  /* The source classifies a rule with a comma-separated list, so a rule that
     both notifies and moves a queue belongs under both filters. */
  const kinds = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of all) {
      for (const k of splitKinds(r.Kinds)) counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [all]);

  const matches = useMemo(() => {
    const needle = term.trim().toLowerCase();
    return all.filter((r) => {
      if (kind && !splitKinds(r.Kinds).includes(kind)) return false;
      if (questionsOnly && r.ReviewNotes.length === 0) return false;
      if (!needle) return true;
      return haystack(r).includes(needle);
    });
  }, [all, term, kind, questionsOnly]);

  if (rules.loading) return <Loading label="Loading the workflow rules" />;
  if (rules.error) return <ErrorBanner message={rules.error} />;

  if (all.length === 0) {
    return (
      <Empty title="No workflow rules loaded">
        The catalogue is imported by migration 096. Run <code>npm run migrate</code> against this
        database and they will appear here.
      </Empty>
    );
  }

  const selected = code === null ? null : (all.find((r) => r.Code === code) ?? null);
  if (selected) {
    return <RuleDetail rule={selected} onBack={() => setCode(null)} />;
  }

  const withNotification = all.filter((r) => r.TemplateName).length;
  const withQuestions = all.filter((r) => r.ReviewNotes.length > 0).length;

  return (
    <section className="wfr">
      <div className="wfr-summary">
        <span>
          <strong className="strong">{all.length}</strong> workflow rules
        </span>
        <span className="muted">·</span>
        <span>
          <strong className="strong">{withNotification}</strong> send a notification
        </span>
        <span className="muted">·</span>
        <button
          type="button"
          className={`wfr-linkish${questionsOnly ? ' on' : ''}`}
          onClick={() => setQuestionsOnly((q) => !q)}
        >
          {withQuestions} carry open questions
        </button>
      </div>

      <div className="wfr-controls">
        <input
          className="input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search triggers, conditions, actions, recipients…"
          aria-label="Search the workflow rules"
        />
        <div className="wfr-kinds">
          <button
            type="button"
            className={`wfr-kind${kind === null ? ' on' : ''}`}
            onClick={() => setKind(null)}
          >
            All
          </button>
          {kinds.map(([k, n]) => (
            <button
              key={k}
              type="button"
              className={`wfr-kind${kind === k ? ' on' : ''}`}
              onClick={() => setKind(kind === k ? null : k)}
            >
              {k} <span className="wfr-count">{n}</span>
            </button>
          ))}
        </div>
      </div>

      {matches.length === 0 ? (
        <Empty title="Nothing matches">
          No rule matches that search. The workbook’s own wording is kept, so try a phrase from the
          PSA — a queue name, a status, an address.
        </Empty>
      ) : (
        <>
          <p className="xs subtle wfr-result-count">
            {matches.length === all.length
              ? `All ${all.length} rules`
              : `${matches.length} of ${all.length} rules`}
          </p>
          <ol className="wfr-index">
            {matches.map((r) => (
              <li key={r.Code}>
                <button type="button" className="wfr-item" onClick={() => setCode(r.Code)}>
                  <span className="mono xs wfr-item-code">{r.Code}</span>
                  <span className="wfr-item-main">
                    <span className="wfr-item-name">{r.Name}</span>
                    <span className="wfr-item-sub xs">
                      {r.TemplateName ? r.TemplateName : firstLine(r.ActionText) || '—'}
                    </span>
                  </span>
                  <span className="wfr-item-kinds">
                    {splitKinds(r.Kinds).map((k) => (
                      <span key={k} className="wfr-tag xs">
                        {k}
                      </span>
                    ))}
                  </span>
                  {r.ReviewNotes.length > 0 && (
                    <span className="wfr-q" title={`${r.ReviewNotes.length} open question(s)`}>
                      {r.ReviewNotes.length}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}

/** One rule, drawn as the flow the spreadsheet tab was trying to draw. */
function RuleDetail({ rule, onBack }: { rule: WorkflowRule; onBack: () => void }) {
  return (
    <section className="wfr">
      <div className="wfr-detail-head">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>
          ← All workflow rules
        </button>
        <span className="mono xs subtle">
          {rule.Code}
          {rule.SourceRow ? ` · source row ${rule.SourceRow}` : ''}
        </span>
      </div>

      <h3 className="wfr-detail-title">{rule.Name}</h3>
      <div className="wfr-item-kinds mb-4">
        {splitKinds(rule.Kinds).map((k) => (
          <span key={k} className="wfr-tag xs">
            {k}
          </span>
        ))}
      </div>

      {rule.ReviewNotes.length > 0 && (
        <div className="wfr-questions">
          <span className="eyebrow">
            {rule.ReviewNotes.length === 1 ? 'Open question' : 'Open questions'}
          </span>
          <ul>
            {rule.ReviewNotes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="wfr-flow">
        <Step label="Trigger" text={rule.TriggerText} />
        <Step label="Condition" text={rule.ConditionText} />
        <Step label="Action" text={rule.ActionText} />
        <Step label="End" text={rule.EndText} last />
      </div>

      {rule.TemplateName ? (
        <div className="wfr-panel">
          <span className="eyebrow">Notification</span>
          <dl className="wfr-kv">
            <dt>Template</dt>
            <dd className="strong">{rule.TemplateName}</dd>
            <dt>Sent to</dt>
            <dd>{recipients(rule.SentTo)}</dd>
            <dt>Audience</dt>
            <dd>{rule.Audience ?? '—'}</dd>
            {rule.TemplateDetails && (
              <>
                <dt>What it is for</dt>
                <dd>{rule.TemplateDetails}</dd>
              </>
            )}
          </dl>
          {rule.HasScreenshot && <Screenshot code={rule.Code} name={rule.TemplateName} />}
        </div>
      ) : (
        <p className="small muted wfr-nonote">This rule sends no notification.</p>
      )}

      <p className="xs subtle wfr-meta">
        {rule.PsaCreatedOn && (
          <>
            Created {formatDate(rule.PsaCreatedOn)}
            {rule.PsaCreatedBy ? ` by ${rule.PsaCreatedBy}` : ''}.{' '}
          </>
        )}
        {rule.PsaUpdatedOn && (
          <>
            Last changed {formatDate(rule.PsaUpdatedOn)}
            {rule.PsaUpdatedBy ? ` by ${rule.PsaUpdatedBy}` : ''}.{' '}
          </>
        )}
        {rule.Associated && <>Associated workflows: {rule.Associated}.</>}
      </p>
    </section>
  );
}

/**
 * The screenshot of the e-mail the rule sends.
 *
 * Collapsed by default. It is a full-width picture of an Outlook window, and
 * on a rule you opened to read the conditions it would push everything else
 * off the screen — but it is also the only place the actual wording of what
 * the client receives is recorded, so it has to be one click away.
 *
 * Loaded only once opened, for the same reason: thirty-four of these are 1.2MB
 * of PNG nobody asked for.
 */
function Screenshot({ code, name }: { code: string; name: string | null }) {
  const [open, setOpen] = useState(false);
  const url = useAuthedImage(open ? `/manual/workflow-rules/${code}/screenshot` : null);

  return (
    <div className="wfr-shot">
      <button
        type="button"
        className="wfr-shot-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? '▾' : '▸'} {open ? 'Hide' : 'Show'} the e-mail this sends
      </button>
      {open &&
        (url ? (
          <img
            className="wfr-shot-img"
            src={url}
            alt={`Screenshot of the ${name ?? 'notification'} e-mail`}
          />
        ) : (
          <p className="xs subtle wfr-shot-loading">Loading the screenshot…</p>
        ))}
    </div>
  );
}

/**
 * A box of the diagram.
 *
 * The PSA writes each of these as a caption line followed by one clause per
 * line — "And the following conditions are met:" then the conditions. The
 * caption becomes the box's lead and the clauses become a list, which is the
 * only reformatting done to any of this text: the wording is the PSA's and
 * people search for it verbatim.
 */
function Step({ label, text, last }: { label: string; text: string | null; last?: boolean }) {
  const lines = (text ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
  const [lead, ...rest] = lines;

  return (
    <div className={`wfr-step${last ? ' last' : ''}`}>
      <span className="wfr-step-label eyebrow">{label}</span>
      <div className="wfr-step-body">
        {lead ? <p className="wfr-step-lead">{lead}</p> : <p className="muted small">Not recorded</p>}
        {rest.length > 0 && (
          <ul className="wfr-clauses">
            {rest.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** The PSA joins recipients with semicolons; a list of six reads as one blob. */
function recipients(value: string | null) {
  if (!value) return '—';
  const parts = value.split(';').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return value;
  return (
    <ul className="wfr-clauses">
      {parts.map((p) => (
        <li key={p}>{p}</li>
      ))}
    </ul>
  );
}

function splitKinds(kinds: string | null): string[] {
  if (!kinds) return [];
  return kinds.split(',').map((k) => k.trim()).filter(Boolean);
}

function firstLine(text: string | null): string {
  if (!text) return '';
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  /* The lead is boilerplate ("Then execute the following actions:"); what the
     rule actually does is the line after it. */
  return lines[1] ?? lines[0] ?? '';
}

function haystack(r: WorkflowRule): string {
  return [
    r.Code,
    r.Name,
    r.Kinds,
    r.TriggerText,
    r.ConditionText,
    r.ActionText,
    r.TemplateName,
    r.SentTo,
    r.Audience,
    r.TemplateDetails,
    r.PsaCreatedBy,
    r.PsaUpdatedBy,
    ...r.ReviewNotes,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}
